/**
 * Help content for EyeconX Clinic — FAQs and support options.
 */

import {
    PAGE_FAQ_CATEGORIES,
    PAGE_FAQ_CATEGORY_GROUPS,
    PAGE_FAQ_ITEMS,
    PAGE_SUPPORT_OPTIONS,
} from './helpPageFaqs';

export const PATIENT_FILES_GROUP_ID = 'patient-files';

export const FAQ_CATEGORY_GROUPS = [
    {
        id: PATIENT_FILES_GROUP_ID,
        title: 'Patient Files',
        icon: 'bi-folder2-open',
        theme: 'indigo',
    },
    ...PAGE_FAQ_CATEGORY_GROUPS,
];

export const FAQ_CATEGORIES = [
    {
        id: 'patient-files-directory',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Directory & Search',
        icon: 'bi-folder2-open',
        theme: 'indigo',
    },
    {
        id: 'patient-profile',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Patient Info',
        icon: 'bi-person-vcard',
        theme: 'indigo',
    },
    {
        id: 'patient-appointments',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Appointments',
        icon: 'bi-calendar-event',
        theme: 'purple',
    },
    {
        id: 'patient-communication',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Communication',
        icon: 'bi-chat-left-text',
        theme: 'success',
    },
    {
        id: 'patient-activity',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Activity',
        icon: 'bi-clock-history',
        theme: 'violet',
    },
    {
        id: 'patient-invalid-info',
        parentId: PATIENT_FILES_GROUP_ID,
        title: 'Invalid Info',
        icon: 'bi-exclamation-octagon',
        theme: 'danger',
    },
    ...PAGE_FAQ_CATEGORIES,
    {
        id: 'my-files',
        title: 'My Files',
        icon: 'bi-folder',
        theme: 'slate',
    },
    {
        id: 'pms-update-history',
        title: 'PMS Update History',
        icon: 'bi-arrow-repeat',
        theme: 'cyan',
    },
    {
        id: 'activity',
        title: 'Activity',
        icon: 'bi-clock-history',
        theme: 'violet',
    },
    {
        id: 'general',
        title: 'General',
        icon: 'bi-life-preserver',
        theme: 'primary',
    },
];

export const FAQ_ITEMS = [
    ...PAGE_FAQ_ITEMS,
    {
        id: 'patient-files-overview',
        categoryId: 'patient-files-directory',
        question: 'What is Patient Files?',
        answer:
            'Patient Files is your clinic directory for searching and opening active patient records. From the list you can sort and paginate results, copy email addresses, and open a patient\'s full profile with appointments, communication preferences, activity history, and invalid contact logs.',
    },
    {
        id: 'patient-files-access',
        categoryId: 'patient-files-directory',
        question: 'How do I open Patient Files?',
        answer:
            'In the left sidebar, click Patient Files. The page opens in search mode—run a search or click Show All Patients to load your clinic\'s active patient directory.',
    },
    {
        id: 'patient-files-search',
        categoryId: 'patient-files-directory',
        question: 'How do I search for a patient?',
        answer:
            'Choose a Search By field: First Name, Last Name, Email, Preferred Name, Home Phone, Cell Phone, or PMS ID. Enter your term and click Search. Partial matches are supported—for example, searching first name "Ann" can return "Anna" and "Joann". PMS ID uses an exact match.',
    },
    {
        id: 'patient-files-show-all',
        categoryId: 'patient-files-directory',
        question: 'What does Show All Patients do?',
        answer:
            'Show All loads every active patient in your clinic without a search filter. Inactive patients are not included in this directory. Use the toolbar to change sort order, direction, or how many rows appear per page.',
    },
    {
        id: 'patient-files-sort',
        categoryId: 'patient-files-directory',
        question: 'How do I sort the patient list?',
        answer:
            'Use the Sort dropdowns in the toolbar to pick a field (First Name, Last Name, Email, Preferred Name, Last Updated, Created Date, or PMS ID) and Asc/Desc order. You can also click sortable column headers in the table—First Name, Last Name, Preferred Name, and Email—to toggle sort direction.',
    },
    {
        id: 'patient-files-pagination',
        categoryId: 'patient-files-directory',
        question: 'How does pagination work?',
        answer:
            'After a search or Show All, the toolbar shows total patients and the current page. Change Show entries to 10, 20, or 50 rows per page. Use the pagination controls below the table to move between pages without losing your current search or sort.',
    },
    {
        id: 'patient-files-open',
        categoryId: 'patient-files-directory',
        question: 'How do I open a patient record?',
        answer:
            'Click anywhere on a patient row or use the View button. You are taken to that patient\'s profile at /patient-files/{patientId}. You can also open a patient from Appointments or other pages that link to Patient Files.',
    },
    {
        id: 'patient-files-copy-email',
        categoryId: 'patient-files-directory',
        question: 'Can I copy a patient email from the list?',
        answer:
            'Yes. In the Email column, click the copy icon next to an address. The icon changes to a check mark when the email is on your clipboard.',
    },
    {
        id: 'patient-files-reset',
        categoryId: 'patient-files-directory',
        question: 'How do I clear my search and start over?',
        answer:
            'Click Reset in the toolbar to clear the loaded directory and return to the initial search screen. You can also clear the search box with the X button and run a new Search.',
    },
    {
        id: 'patient-files-empty',
        categoryId: 'patient-files-directory',
        question: 'Why is the list empty?',
        answer:
            'On first visit, Patient Files waits for you to search or click Show All Patients. If you searched and see no results, try another field or a shorter partial term. Confirm the patient is active—inactive patients do not appear in the directory.',
    },
    {
        id: 'patient-files-status',
        categoryId: 'patient-files-directory',
        question: 'What do Active and Welcome Sent mean in the list?',
        answer:
            'Active means the patient record is currently active in EyeconX. Inactive patients are hidden from the directory but can still be opened by direct link if you have their ID. Welcome Sent indicates a welcome communication has already been sent to that patient.',
    },
    {
        id: 'patient-profile-overview',
        categoryId: 'patient-profile',
        question: 'What is on the patient profile page?',
        answer:
            'The profile page shows a summary header with name, PMS ID, date of birth, gender, active status, welcome status, contact cards, and address. Below that, tabs let you manage Patient Info and Communication. Use the Actions menu on the right of those tabs to send email, SMS, surveys, and other patient messages. On Patient Info, profile details appear first, then Appointments, Activity, and Invalid Info stacked below so you can scroll through them.',
    },
    {
        id: 'patient-profile-back',
        categoryId: 'patient-profile',
        question: 'How do I return to the patient list?',
        answer:
            'Click Back to Patient Files in the page header. Your previous search or Show All state is preserved in the app until you click Reset on the list page.',
    },
    {
        id: 'patient-profile-summary',
        categoryId: 'patient-profile',
        question: 'What can I do in the patient summary header?',
        answer:
            'Review the patient\'s identity, PMS ID, age, gender, and status pills. Copy email, cell, or home phone using the copy buttons on each contact card. The address section shows the patient\'s mailing address when available.',
    },
    {
        id: 'patient-profile-quick-actions',
        categoryId: 'patient-profile',
        question: 'Why are quick action buttons disabled?',
        answer:
            'Quick actions such as Send Email, Send SMS, Send Survey, and Send Articles are in the Actions menu on the right of the Patient Info / Communication tabs. They require the right contact detail on file and the matching communication preference to be enabled. Welcome is also disabled after a welcome message has already been sent. Hover a disabled item to see the reason.',
    },
    {
        id: 'patient-profile-info-tab',
        categoryId: 'patient-profile',
        question: 'What can I edit on the Patient Info tab?',
        answer:
            'Click Edit to update email, cell number, home phone, and the Active patient toggle. First name, last name, birth date, age, and gender are read-only—they sync from your practice management system. Click Save to apply changes or Cancel to discard them.',
    },
    {
        id: 'patient-profile-active',
        categoryId: 'patient-profile',
        question: 'What happens when I mark a patient inactive?',
        answer:
            'Inactive patients no longer appear in the Patient Files directory or Show All list. Their record still exists and can be reopened by direct link. Turn Active back on from the Patient Info tab if the patient should return to the directory.',
    },
    {
        id: 'patient-profile-not-found',
        categoryId: 'patient-profile',
        question: 'Why does the profile say Patient not found?',
        answer:
            'The patient ID in the URL may be invalid, the record may have been removed, or your clinic may not have access to that patient. Use Return to Patient Files and search again, or confirm you have the correct link.',
    },
    {
        id: 'patient-appointments-overview',
        categoryId: 'patient-appointments',
        question: 'What does the Appointments section show?',
        answer:
            'Under Patient Info, scroll to Appointments to list upcoming and past appointments for the current patient. Each row includes date and time, doctor, duration, confirmation status, and reminder delivery icons for enabled reminder types (Prebooked, First, Second, Last, Same Day).',
    },
    {
        id: 'patient-appointments-today',
        categoryId: 'patient-appointments',
        question: 'What does the Today badge mean?',
        answer:
            'A yellow Today badge appears next to appointments scheduled for the current calendar day, making it easy to spot same-day visits in the patient\'s history.',
    },
    {
        id: 'patient-appointments-status',
        categoryId: 'patient-appointments',
        question: 'What do appointment status badges mean?',
        answer:
            'Not Confirmed means the appointment has not been confirmed in EyeconX. Confirmed by Eyeconx and Confirmed by PMS indicate confirmation through EyeconX or your practice management system. Cancelled means the appointment was marked cancelled.',
    },
    {
        id: 'patient-appointments-reminders',
        categoryId: 'patient-appointments',
        question: 'How do I read reminder icons?',
        answer:
            'Colored icons show which reminder types are enabled for that appointment and whether a reminder was sent. Hover an icon for details such as the reminder label, send date, and channel (Email, SMS, Call, or Live Call). Icons only appear for reminder types that are active on the appointment.',
    },
    {
        id: 'patient-appointments-pagination',
        categoryId: 'patient-appointments',
        question: 'Why do I only see some appointments?',
        answer:
            'Appointments load in pages of 10. Use the pagination controls at the bottom to view older or additional records. For the full clinic schedule on a specific day, open Appointments from the main sidebar.',
    },
    {
        id: 'patient-appointments-clinic-page',
        categoryId: 'patient-appointments',
        question: 'How is this different from the main Appointments page?',
        answer:
            'The patient Appointments section shows history for one patient only. The main Appointments page shows all clinic appointments for a selected date, with ECP filtering, status filters, print, and confirm/cancel actions for that day\'s schedule.',
    },
    {
        id: 'patient-communication-overview',
        categoryId: 'patient-communication',
        question: 'What are communication preferences?',
        answer:
            'Communication preferences control which channels and notification types EyeconX may use for a patient. Channels include Email, SMS, and Phone Call, each with a priority (Primary, Secondary, or Tertiary). Notifications cover Reminders, Newsletter, Articles, Recall, Glasses, Lenses, Lens Orders, Survey, Thank You, Follow-up, and Birthday.',
    },
    {
        id: 'patient-communication-channels',
        categoryId: 'patient-communication',
        question: 'How do I edit communication channels?',
        answer:
            'On the Communication tab, click Edit in the Communication Channels section. Toggle each channel on or off and set its priority. Use Enable All or Disable All for quick changes. Save applies only that section; Cancel restores the last saved values.',
    },
    {
        id: 'patient-communication-notifications',
        categoryId: 'patient-communication',
        question: 'How do I edit notification types?',
        answer:
            'Click Edit in the Notifications & Content section. Toggle individual notification types on or off, or use Enable All / Disable All. Save to store your changes. Only one section can be edited at a time—finish or cancel before editing the other.',
    },
    {
        id: 'patient-communication-effect',
        categoryId: 'patient-communication',
        question: 'How do preferences affect outreach?',
        answer:
            'Disabled channels or notification types can block automated sends and disable related quick actions on the patient summary. For example, if SMS is disabled, Send SMS quick action is unavailable. Update contact details on the Patient Info tab if a channel is enabled but contact information is missing.',
    },
    {
        id: 'patient-communication-priority',
        categoryId: 'patient-communication',
        question: 'What is channel priority for?',
        answer:
            'Primary, Secondary, and Tertiary priority ranks Email, SMS, and Phone Call when EyeconX chooses how to reach a patient. Set the patient\'s preferred channel as Primary and backup channels as Secondary or Tertiary.',
    },
    {
        id: 'patient-activity-overview',
        categoryId: 'patient-activity',
        question: 'What does the Activity section show?',
        answer:
            'Under Patient Info, scroll to Activity to see communication and system events for the patient—such as emails, SMS, calls, and other logged interactions. Each entry shows a summary in the list; open View Details for the full activity record in a modal.',
    },
    {
        id: 'patient-activity-details',
        categoryId: 'patient-activity',
        question: 'How do I view activity details?',
        answer:
            'Click an activity row or its View Details control. A modal opens with expanded information about that event. Close the modal to return to the list.',
    },
    {
        id: 'patient-activity-pagination',
        categoryId: 'patient-activity',
        question: 'How much activity history is shown?',
        answer:
            'Activity loads 20 records per page. Use pagination at the bottom to browse older events. For clinic-wide activity, open Activity from the Misc section in the sidebar.',
    },
    {
        id: 'patient-activity-empty',
        categoryId: 'patient-activity',
        question: 'Why is there no activity for this patient?',
        answer:
            'No logged events exist yet for that patient, or activity has not synced. New communications and system actions will appear here as they are recorded.',
    },
    {
        id: 'patient-invalid-overview',
        categoryId: 'patient-invalid-info',
        question: 'What is the Invalid Info section?',
        answer:
            'Under Patient Info, scroll to Invalid Info to see contact problems logged for this patient—such as bounced emails or invalid phone numbers detected during delivery or validation. Use it to review bad data tied to a specific patient without leaving their profile.',
    },
    {
        id: 'patient-invalid-types',
        categoryId: 'patient-invalid-info',
        question: 'What types of invalid details are tracked?',
        answer:
            'Invalid Email (red), Invalid Cell Phone (yellow), and Invalid Home Phone (gray). Each row shows the invalid value, patient name, and the date it was logged.',
    },
    {
        id: 'patient-invalid-delete',
        categoryId: 'patient-invalid-info',
        question: 'How do I remove invalid detail records?',
        answer:
            'Delete a single row with the trash icon on that row, or select multiple checkboxes and click Delete Selected. Confirm in the dialog—removed records no longer appear in this patient\'s invalid log. Fix the underlying contact on the Patient Info tab so future sends use valid details.',
    },
    {
        id: 'patient-invalid-empty',
        categoryId: 'patient-invalid-info',
        question: 'What does "No invalid details found" mean?',
        answer:
            'No invalid contact records are currently logged for this patient. That is expected when email and phone details are valid or have not failed validation.',
    },
    {
        id: 'patient-invalid-clinic-page',
        categoryId: 'patient-invalid-info',
        question: 'How is this related to Invalid Details in the sidebar?',
        answer:
            'The Invalid Info section is per patient. Invalid Details under Misc in the sidebar is a clinic-wide view where you can search, filter, sort, and print all invalid records across patients. Deleting from either place removes that log entry.',
    },
    {
        id: 'my-files-overview',
        categoryId: 'my-files',
        question: 'What is My Files?',
        answer:
            'My Files is your clinic\'s media library in EyeconX. Upload and manage images, PDFs, and other supported assets in one place. Each file gets a hosted URL you can copy and reuse across EyeconX—for example in articles, newsletters, or other content that needs a stable link to clinic media.',
    },
    {
        id: 'my-files-access',
        categoryId: 'my-files',
        question: 'How do I open My Files?',
        answer:
            'In the left sidebar, open the Misc section and click My Files. The page header shows your total file count and includes a Refresh button to reload the library after uploads or deletions.',
    },
    {
        id: 'my-files-upload-button',
        categoryId: 'my-files',
        question: 'How do I upload a file?',
        answer:
            'Click Upload Media in the toolbar (or Upload on smaller screens). In the upload window, drag a file onto the drop zone or click to browse your device. You can also drag a file anywhere on the My Files page—a drop overlay appears and the upload starts when you release the file. Only one file is uploaded at a time.',
    },
    {
        id: 'my-files-formats',
        categoryId: 'my-files',
        question: 'Which file types are supported?',
        answer:
            'Supported uploads include common image formats (JPEG, PNG, GIF, BMP, TIFF, ICO, HEIF, HEIC, and similar image types) and PDF documents. If a file type is not supported, the upload fails with an error—convert the file to a supported format and try again.',
    },
    {
        id: 'my-files-upload-progress',
        categoryId: 'my-files',
        question: 'What happens while a file is uploading?',
        answer:
            'The upload modal shows a progress bar and percentage while the file is sent to EyeconX. The modal cannot be closed until the upload finishes. When complete, the modal closes automatically and your library refreshes so the new file appears in the grid.',
    },
    {
        id: 'my-files-search',
        categoryId: 'my-files',
        question: 'How do I search my library?',
        answer:
            'Use the Search files field in the toolbar. Type part of a file name and press Enter or click Search. Matching files are shown and the file count chip updates. Click Clear to remove the filter and show the full library again.',
    },
    {
        id: 'my-files-sort',
        categoryId: 'my-files',
        question: 'How can I sort files?',
        answer:
            'Open the sort dropdown in the toolbar. Choose Newest First (default), Oldest First, Name A–Z, or Name Z–A. Sorting applies to the full library and works together with search—changing sort or search resets pagination to the first page.',
    },
    {
        id: 'my-files-pagination',
        categoryId: 'my-files',
        question: 'Why do I only see some files on the page?',
        answer:
            'My Files shows files in a responsive grid (two to four columns depending on screen width) with three rows per page. Use the pagination controls at the bottom to move between pages. Resizing the browser may change how many files fit on each page.',
    },
    {
        id: 'my-files-preview',
        categoryId: 'my-files',
        question: 'Can I preview a file before using it?',
        answer:
            'Yes. Click the thumbnail on a file card to open a preview. Images open with zoom controls (buttons or Ctrl / ⌘ + scroll). PDFs open in an in-app viewer with the browser’s built-in PDF controls. From either preview you can also open the file in a new browser tab.',
    },
    {
        id: 'my-files-copy-url',
        categoryId: 'my-files',
        question: 'How do I copy a file URL?',
        answer:
            'Each file card shows the full hosted URL in a read-only field. Click the copy button next to the URL—the icon changes to a check mark when the link is copied to your clipboard. Paste that URL wherever EyeconX or your content needs a direct link to the file.',
    },
    {
        id: 'my-files-delete',
        categoryId: 'my-files',
        question: 'How do I delete a file?',
        answer:
            'Click the trash icon on the file card. Confirm in the dialog—deletion is permanent and removes the file from your clinic media library. Any content that still references the old URL may show a broken link, so update those references before deleting if the file is in use.',
    },
    {
        id: 'my-files-refresh',
        categoryId: 'my-files',
        question: 'When should I use Refresh?',
        answer:
            'Click Refresh in the page header if the library looks out of date—for example after another team member uploaded or deleted files, or if a file did not appear immediately after upload. Refresh reloads the full list from the server without leaving the page.',
    },
    {
        id: 'my-files-empty',
        categoryId: 'my-files',
        question: 'Why does my library look empty?',
        answer:
            'If no files have been uploaded yet, My Files shows an empty state with an Upload Media button. If you applied a search filter, clear it to see all files again. If you expected files from an older workflow, confirm you are signed in to the correct clinic.',
    },
    {
        id: 'my-files-upload-failed',
        categoryId: 'my-files',
        question: 'Why did my upload fail?',
        answer:
            'Common causes include an unsupported file type, a network interruption, or a temporary server error. Check that the file is a supported image or PDF format and try again. If the problem continues, use Refresh, try a smaller file, or contact EyeconX support with the file type and approximate size.',
    },
    {
        id: 'my-files-metadata',
        categoryId: 'my-files',
        question: 'What information is shown on each file card?',
        answer:
            'Each card shows a thumbnail (or a document icon for non-images), the original file name, last modified date, file size, file type badge, the hosted URL, and actions to copy the link or delete the file.',
    },
    {
        id: 'pms-history-overview',
        categoryId: 'pms-update-history',
        question: 'What is PMS Update History?',
        answer:
            'PMS Update History shows a log of sync runs between your Practice Management System (PMS) and EyeconX. Each entry records when a sync occurred, whether it succeeded or failed, which PMS type was involved, and notes with additional detail. Use it to confirm patient and appointment data is updating and to troubleshoot failed syncs.',
    },
    {
        id: 'pms-history-access',
        categoryId: 'pms-update-history',
        question: 'How do I open PMS Update History?',
        answer:
            'Open your account panel from the top-right (click your profile), then under Account Settings click PMS Update History. The page loads your clinic\'s sync log automatically, with the most recent sync at the top.',
    },
    {
        id: 'pms-history-latest-sync',
        categoryId: 'pms-update-history',
        question: 'What does Latest sync in the header mean?',
        answer:
            'Latest sync shows the timestamp of the most recent sync record on the current first page—typically your clinic\'s last completed PMS update. If no history exists yet, it displays a dash until the first sync is logged.',
    },
    {
        id: 'pms-history-header',
        categoryId: 'pms-update-history',
        question: 'What does the page header show?',
        answer:
            'Below the title, the subtitle summarizes your sync log—for example, "Practice Management System sync logs · 42 total records." When records exist, Latest sync in the top-right shows when your most recent sync ran. Use the Help button beside it to open PMS Update History FAQs on the Help & Support page.',
    },
    {
        id: 'pms-history-toolbar',
        categoryId: 'pms-update-history',
        question: 'What do the chips in the Sync History toolbar mean?',
        answer:
            'The list panel header shows Sync History with two optional chips: Page X of Y for your current pagination position, and a laptop icon chip listing PMS types that appear on the current page (for example, your connector name). Use Show entries on the right to change how many rows load per page.',
    },
    {
        id: 'pms-history-data',
        categoryId: 'pms-update-history',
        question: 'What data does a PMS sync update in EyeconX?',
        answer:
            'Automated PMS syncs pull patient and appointment data from your Practice Management System into EyeconX—names, contact details, appointment dates, doctors, and related fields your integration supports. PMS Update History does not show every changed record; it logs each sync run and whether it completed successfully.',
    },
    {
        id: 'pms-history-list-item',
        categoryId: 'pms-update-history',
        question: 'What does each sync record show?',
        answer:
            'Each row shows the sync timestamp, a Success or Failed status badge, the PMS type (when available), and a Check logs button. Records are sorted newest first so recent activity appears at the top of the list.',
    },
    {
        id: 'pms-history-status',
        categoryId: 'pms-update-history',
        question: 'What do Success and Failed mean?',
        answer:
            'Success means the PMS sync completed without error. Failed means the sync ended with an error—open Check logs on that row and read the Notes section for the error message. Contact EyeconX support with the sync timestamp and notes if failures continue.',
    },
    {
        id: 'pms-history-check-logs',
        categoryId: 'pms-update-history',
        question: 'How do I view sync log details?',
        answer:
            'Click Check logs on any sync row. The Sync Log Details modal shows the date and time, result, PMS type, Sync ID, Clinic ID, and a Notes field with the full sync message. Failed syncs highlight notes in red. Close the modal when finished.',
    },
    {
        id: 'pms-history-loading',
        categoryId: 'pms-update-history',
        question: 'Why does the list show gray placeholders while loading?',
        answer:
            'Animated skeleton rows appear while sync history is fetched from the server. They are replaced by real records once loading completes. If loading fails, you see a toast error—refresh the page or navigate away and back to try again.',
    },
    {
        id: 'pms-history-pms-type',
        categoryId: 'pms-update-history',
        question: 'What is the PMS type badge?',
        answer:
            'The PMS type identifies which Practice Management System connector ran the sync (for example, your configured PMS integration name). The toolbar may also show PMS types present on the current page as a quick reference.',
    },
    {
        id: 'pms-history-pagination',
        categoryId: 'pms-update-history',
        question: 'How do I browse older sync records?',
        answer:
            'Use the pagination controls at the bottom of the list when more than one page exists. The toolbar shows the current page number and total pages. Change Show entries to 10, 25, or 50 rows per page—changing page size returns you to page 1.',
    },
    {
        id: 'pms-history-empty',
        categoryId: 'pms-update-history',
        question: 'Why is there no sync history?',
        answer:
            'If your clinic has not completed a PMS sync yet, the page shows an empty state: "No PMS sync history yet." Once your PMS integration runs its first update, entries will appear here automatically. Confirm your PMS connector is configured and running if you expected history sooner.',
    },
    {
        id: 'pms-history-failed',
        categoryId: 'pms-update-history',
        question: 'What should I do after a failed sync?',
        answer:
            'Open Check logs on the failed row and review Notes for the specific error. Verify your PMS integration is online and that recent changes exist in the PMS to sync. If the next scheduled sync succeeds, no action may be needed. For repeated failures, email EyeconX support with the sync timestamp, PMS type, and notes from the log.',
    },
    {
        id: 'pms-history-vs-activity',
        categoryId: 'pms-update-history',
        question: 'How is this different from Activity?',
        answer:
            'PMS Update History tracks automated sync jobs between your PMS and EyeconX—success/failure and technical notes per sync run. Activity logs patient-facing communications and other clinic events (emails, SMS, calls, etc.). Use PMS Update History for data sync health; use Activity for outreach history.',
    },
    {
        id: 'activity-overview',
        categoryId: 'activity',
        question: 'What is Activity?',
        answer:
            'Activity is your clinic-wide communication log in EyeconX. It lists emails, SMS, calls, and other outreach events sent to patients or users—reminders, recalls, welcome messages, confirmations, and more. Each entry shows the activity name, delivery status, channel, receiver, and timestamp. Use it to review what was sent, confirm delivery, and troubleshoot failed communications.',
    },
    {
        id: 'activity-access',
        categoryId: 'activity',
        question: 'How do I open Activity?',
        answer:
            'In the left sidebar, open the Misc section and click Activity. The page loads your clinic\'s activity log automatically, grouped by date with the most recent events first.',
    },
    {
        id: 'activity-layout',
        categoryId: 'activity',
        question: 'What is on the Activity page?',
        answer:
            'The page has three main areas: a Category panel on the left to filter by activity type, a filter bar above the log for search and quick filters, and the Activity Log list on the right. The header shows your total record count and a Refresh button. Use the Help button in the header to open Activity FAQs on Help & Support.',
    },
    {
        id: 'activity-category',
        categoryId: 'activity',
        question: 'How do I filter by category?',
        answer:
            'Use the Category panel on the left. Choose All to see every type, or pick a specific category such as Reminder, Recall, Welcome, Confirmation, Patient Engagement, Clinic Engagement, Lab, Summary, or Post Exam. Selecting a category applies immediately and resets to page 1. On smaller screens, tap the Category header to expand or collapse the panel.',
    },
    {
        id: 'activity-search',
        categoryId: 'activity',
        question: 'How do I search by patient name?',
        answer:
            'Type in the Search patient name field at the top of the filter bar and click Search (or press Enter). The search matches patient name using a starts-with filter. Combine it with category, medium, status, or date filters to narrow results further.',
    },
    {
        id: 'activity-medium-status',
        categoryId: 'activity',
        question: 'What do the Medium and Status filters do?',
        answer:
            'Medium pills filter by channel: All, Email, SMS, or Call. Status pills filter by delivery state: All, Sent, Failed, Queued, Waiting, or Resend. Click a pill to apply that filter immediately—no Search click required. Active medium and status selections also appear as removable chips below the filter bar.',
    },
    {
        id: 'activity-date-filter',
        categoryId: 'activity',
        question: 'How do I filter by date?',
        answer:
            'Use the Date picker in the quick filter row to limit activity to a single day—this applies immediately. For more control, open Filters and switch to Date range under the expanded panel. Choose Single day or Date range, then set a from/to range and click Apply filters. Single-day and range filters cannot be active at the same time.',
    },
    {
        id: 'activity-expanded-filters',
        categoryId: 'activity',
        question: 'What are in the expanded Filters panel?',
        answer:
            'Click Filters to expand advanced options. Patient details includes First name, Last name, and Preferred name (each uses starts-with matching). Activity details includes Activity name and Receiver (email or phone). Date range lets you pick a single day or a from/to range. Click Apply filters to run your selections, or Clear all to reset everything.',
    },
    {
        id: 'activity-filter-chips',
        categoryId: 'activity',
        question: 'How do I clear active filters?',
        answer:
            'Applied filters appear as chips below the filter bar—click the X on any chip to remove that filter. Use Clear all to reset every filter, category, and date selection at once. If you changed values in the expanded panel but have not applied them yet, Apply filters highlights when pending changes exist.',
    },
    {
        id: 'activity-log-rows',
        categoryId: 'activity',
        question: 'What does each activity row show?',
        answer:
            'Each row is a single line with the activity name, status badge (Sent, Failed, Queued, Waiting, or Resend), medium badge (Email, SMS, or Call), a Details button, and the time on the right. Rows are grouped under date headers such as Today, Yesterday, or a formatted date.',
    },
    {
        id: 'activity-date-groups',
        categoryId: 'activity',
        question: 'How are activities grouped by date?',
        answer:
            'The Activity Log groups records under date section headers. Recent days show as Today or Yesterday; older dates show the weekday and full date. Within each group, activities follow your current sort order—by default, newest first within the page.',
    },
    {
        id: 'activity-details',
        categoryId: 'activity',
        question: 'How do I view activity details?',
        answer:
            'Click Details on any row to open the Activity Preview modal. The header shows the activity name, status, medium, category, and full timestamp. A summary section lists Patient, Receiver, and Modified when available. Below that, the preview panel shows the message content when stored—HTML for emails, a message bubble for SMS, or call details for voice activity.',
    },
    {
        id: 'activity-receiver-link',
        categoryId: 'activity',
        question: 'When can I open a patient from Activity?',
        answer:
            'In the Activity Preview modal, Patient and Receiver link to Patient Files when the activity was sent to a patient (patientId is present). Click the linked name to open that patient\'s profile. Activities sent to staff or other users show the receiver as plain text with no link, since there is no patient record.',
    },
    {
        id: 'activity-preview',
        categoryId: 'activity',
        question: 'What appears in the activity preview?',
        answer:
            'Email activities render stored HTML content when available. SMS activities show the message text in a chat-style bubble. Call activities show a voice-call summary with the stored script or notes. If no content was saved, the modal displays a "No preview available" message—you can still review status, receiver, and timestamps in the summary.',
    },
    {
        id: 'activity-status',
        categoryId: 'activity',
        question: 'What do status badges mean?',
        answer:
            'Sent means the message was delivered successfully. Failed means delivery ended with an error—open Details to review the activity and check Invalid Details or patient contact info if needed. Queued and Waiting mean the message is still pending send. Resend indicates a retry was queued after a previous attempt.',
    },
    {
        id: 'activity-sort',
        categoryId: 'activity',
        question: 'How do I sort the activity log?',
        answer:
            'Use the Sort dropdowns in the Activity Log toolbar. Choose a field—Created date (default), Modified date, First name, Last name, Category, Activity name, Status, or Medium—and Asc or Desc order. Changing sort returns you to page 1.',
    },
    {
        id: 'activity-pagination',
        categoryId: 'activity',
        question: 'How does pagination work?',
        answer:
            'The Activity Log toolbar shows Page X of Y. Change Show entries to 10, 20, or 50 rows per page. Use the pagination controls at the bottom when more than one page exists. Filters, category, and sort are kept when you move between pages.',
    },
    {
        id: 'activity-refresh',
        categoryId: 'activity',
        question: 'When should I use Refresh?',
        answer:
            'Click Refresh in the page header after new communications are sent or when the log may be out of date. Refresh reloads the current page using your active filters and sort without leaving Activity.',
    },
    {
        id: 'activity-empty',
        categoryId: 'activity',
        question: 'Why does the log say "No activity found"?',
        answer:
            'No records match your current filters or category. Try Clear all, set Category to All, widen your date range, or remove medium/status restrictions. If you expected recent outreach, click Refresh or confirm the communication was sent from EyeconX.',
    },
    {
        id: 'activity-vs-pms',
        categoryId: 'activity',
        question: 'How is Activity different from PMS Update History?',
        answer:
            'Activity logs outbound communications and clinic outreach events—emails, SMS, calls, and related delivery status. PMS Update History logs automated sync runs between your Practice Management System and EyeconX. Use Activity to audit messages sent to patients; use PMS Update History to check data sync health.',
    },
    {
        id: 'activity-vs-patient-tab',
        categoryId: 'activity',
        question: 'How is this different from Activity on a patient profile?',
        answer:
            'The main Activity page shows communication history across your entire clinic with category panels, advanced filters, and sorting. Activity under Patient Info on a patient profile shows only that patient\'s events in a simpler list. Use the clinic Activity page for broad searches; use the patient section when reviewing one person\'s history from their profile.',
    },
    {
        id: 'general-help',
        categoryId: 'general',
        question: 'Where can I find Help & Support?',
        answer:
            'Open Help & Support from the left sidebar (above Logout), or from the Support section in your right account panel (click your profile in the top-right). Browse FAQs by category groups (Patient Files, Scheduling, Engagement, Communication, Settings), search by keyword, or switch to Get Support for email contact and quick links to common pages.',
    },
    {
        id: 'general-browse-faqs',
        categoryId: 'general',
        question: 'How do I browse FAQs for a specific page?',
        answer:
            'On the FAQs tab, use the category chips or expand a group such as Scheduling, Engagement, Communication, or Settings. You can also type a page name (for example “Reputation” or “Web Bookings”) into the search box to jump to matching questions.',
    },
    {
        id: 'general-missing-feature',
        categoryId: 'general',
        question: 'Why don’t I see a page mentioned in Help?',
        answer:
            'Many pages only appear when their clinic feature is enabled and your role allows access. Check Settings → Features, then Settings → Users for your role. Get Support shortcuts also hide when a feature is turned off.',
    },
    {
        id: 'general-password',
        categoryId: 'general',
        question: 'How do I change my password?',
        answer:
            'Open your account panel from the top-right (click your profile), then under Account Settings click Password Settings. You can also use the Change Password shortcut under Get Support on this page. Enter your current password and a new password that meets the requirements. You may be signed out after a successful change.',
    },
];

const SUPPORT_EMAIL = process.env.REACT_APP_SUPPORT_EMAIL || 'support@eyeconx.com';

export const HELP_THEME_BUTTON_COLORS = {
    primary: 'primary',
    indigo: 'primary',
    purple: 'primary',
    cyan: 'info',
    success: 'success',
    slate: 'secondary',
    violet: 'primary',
    danger: 'danger',
    warning: 'warning',
    teal: 'success',
    pink: 'primary',
    orange: 'warning',
    info: 'info',
};

export const SUPPORT_OPTIONS = [
    {
        id: 'email',
        title: 'Email Support',
        description: 'Send questions about any EyeconX Clinic page, feature, or workflow.',
        icon: 'bi-envelope',
        theme: 'pink',
        color: 'primary',
        actionLabel: 'Email support',
        href: `mailto:${SUPPORT_EMAIL}?subject=EyeconX%20Support`,
        external: true,
    },
    {
        id: 'patient-files',
        title: 'Patient Files',
        description: 'Search active patients, open profiles, and manage contact and communication settings.',
        icon: 'bi-folder2-open',
        theme: 'indigo',
        color: 'primary',
        actionLabel: 'Go to Patient Files',
        to: '/patient-files',
    },
    ...PAGE_SUPPORT_OPTIONS,
    {
        id: 'invalid-details',
        title: 'Invalid Details',
        description: 'Review clinic-wide invalid email and phone logs across all patients.',
        icon: 'bi-exclamation-octagon',
        theme: 'danger',
        color: 'danger',
        actionLabel: 'Go to Invalid Details',
        to: '/invalid-details',
        featureKey: 'isInvalidSummary',
    },
    {
        id: 'appointments',
        title: 'Appointments',
        description: 'View the clinic schedule by date, filter by ECP, and manage confirmation status.',
        icon: 'bi-calendar-event',
        theme: 'purple',
        color: 'primary',
        actionLabel: 'Go to Appointments',
        to: '/appointments',
        featureKey: 'isAppointments',
    },
    {
        id: 'articles',
        title: 'Articles',
        description: 'Create and send patient education articles.',
        icon: 'bi-file-text',
        theme: 'orange',
        color: 'warning',
        actionLabel: 'Go to Articles',
        to: '/articles',
        featureKey: 'isArticle',
    },
    {
        id: 'optical-dispensary',
        title: 'Optical Dispensary',
        description: 'Manage pickups, contact lens reorder requests, and CL brands.',
        icon: 'bi-eyeglasses',
        theme: 'violet',
        color: 'primary',
        actionLabel: 'Go to Optical Dispensary',
        to: '/optical-dispensary/pickups',
        featureKey: 'isOpticalDispensary',
    },
    {
        id: 'my-files',
        title: 'My Files',
        description: 'Upload clinic media, copy hosted URLs, preview images, and manage your library.',
        icon: 'bi-folder',
        theme: 'slate',
        color: 'secondary',
        actionLabel: 'Go to My Files',
        to: '/my-files',
    },
    {
        id: 'pms-update-history',
        title: 'PMS Update History',
        description: 'Open from your right account panel to review PMS sync logs, status, and notes.',
        icon: 'bi-arrow-repeat',
        theme: 'cyan',
        color: 'info',
        actionLabel: 'Go to PMS Update History',
        to: '/pms-update-history',
    },
    {
        id: 'activity',
        title: 'Activity',
        description: 'Search clinic communication history, filter by category and status, and preview message details.',
        icon: 'bi-clock-history',
        theme: 'violet',
        color: 'primary',
        actionLabel: 'Go to Activity',
        to: '/activity',
    },
    {
        id: 'password',
        title: 'Change Password',
        description: 'Open Password Settings from your right account panel to update your login password.',
        icon: 'bi-shield-lock',
        theme: 'info',
        color: 'secondary',
        actionLabel: 'Change password',
        to: '/update-password',
    },
];

export const getFaqItems = () => FAQ_ITEMS;

export const getSupportOptions = (clinicFeatures, clinicFeaturesLoaded) =>
    SUPPORT_OPTIONS.filter((option) => {
        if (!option.featureKey) {
            return true;
        }

        if (!clinicFeaturesLoaded) {
            return false;
        }

        return clinicFeatures?.[option.featureKey] === true;
    });

export const getCategoryById = (categoryId) =>
    FAQ_CATEGORIES.find((category) => category.id === categoryId) || null;

export const getCategoryGroupById = (groupId) =>
    FAQ_CATEGORY_GROUPS.find((group) => group.id === groupId) || null;

export const getTopLevelCategories = () =>
    FAQ_CATEGORIES.filter((category) => !category.parentId);

export const getChildCategories = (parentId) =>
    FAQ_CATEGORIES.filter((category) => category.parentId === parentId);

export const categoryMatchesFilter = (categoryId, filter) => {
    if (filter === 'all') {
        return true;
    }

    if (categoryId === filter) {
        return true;
    }

    const category = getCategoryById(categoryId);

    return Boolean(category?.parentId === filter);
};

export const buildFaqCategorySections = (faqItems, filter = 'all') => {
    const sections = [];

    FAQ_CATEGORY_GROUPS.forEach((group) => {
        const children = getChildCategories(group.id)
            .map((category) => ({
                ...category,
                items: faqItems.filter(
                    (item) =>
                        item.categoryId === category.id && categoryMatchesFilter(category.id, filter)
                ),
            }))
            .filter((category) => category.items.length > 0);

        if (!children.length) {
            return;
        }

        if (filter !== 'all' && filter !== group.id && !children.some((child) => child.id === filter)) {
            return;
        }

        const visibleChildren =
            filter === group.id
                ? children
                : children.filter((child) => categoryMatchesFilter(child.id, filter));

        if (!visibleChildren.length) {
            return;
        }

        sections.push({
            type: 'group',
            ...group,
            children: visibleChildren,
        });
    });

    getTopLevelCategories().forEach((category) => {
        const items = faqItems.filter(
            (item) =>
                item.categoryId === category.id && categoryMatchesFilter(category.id, filter)
        );

        if (!items.length) {
            return;
        }

        sections.push({
            type: 'category',
            ...category,
            items,
        });
    });

    return sections;
};
